import axios from 'axios';
import { getHeaders } from 'auth/auth';
import { api } from 'constants/constant';

const EXTENSION_MIME_MAP = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  gif: 'image/gif',
  svg: 'image/svg+xml',
  webp: 'image/webp',
  heic: 'image/heic',
};

const buildBaseUrl = (path) => {
  const base = (api || '').replace(/\/+$/, '');
  const normalizedPath = path.replace(/^\/+/, '');
  return `${base}/${normalizedPath}`;
};

const buildHeaders = ({ removeContentType = false } = {}) => {
  const headers = getHeaders();
  if (removeContentType) {
    delete headers['Content-Type'];
    delete headers['content-type'];
  }
  return headers;
};

const decodeURIComponentSafe = (value) => {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

const extractFileName = (contentDisposition = '') => {
  const filenameExtendedMatch = contentDisposition.match(
    /filename\*=(?:UTF-8'')?["']?([^";]+)["']?/i
  );

  if (filenameExtendedMatch && filenameExtendedMatch[1]) {
    return decodeURIComponentSafe(filenameExtendedMatch[1].trim());
  }

  const filenameMatch = contentDisposition.match(/filename=["']?([^";]+)["']?/i);

  if (filenameMatch && filenameMatch[1]) {
    return decodeURIComponentSafe(filenameMatch[1].trim());
  }

  return null;
};

const getMimeFromExtension = (extension) => {
  if (!extension) {
    return null;
  }

  return EXTENSION_MIME_MAP[extension.toLowerCase()] || null;
};

/* The name the server gave the file, out of Content-Disposition.
 *
 * The header is already exposed to the browser through CORS — `resolveMimeType`
 * below reads it to work out the type — so the name was there all along and
 * was simply being dropped. Without it a download falls back to "document",
 * and a borrower saving their pre-approval letter gets "document (1)".
 *
 * Handles both `filename="x.pdf"` and the RFC 5987 `filename*=UTF-8''x.pdf`
 * form, preferring the latter where a server sends both.
 */
export const filenameFromResponse = (response) => {
  const header = response?.headers?.['content-disposition'];
  if (!header) return null;

  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // A malformed encoding is not worth failing a download over.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || null;
};

const resolveMimeType = (response) => {
  const fallbackType = 'application/octet-stream';
  const explicitType = response.headers['content-type'];

  if (explicitType && explicitType !== fallbackType) {
    return explicitType;
  }

  const contentDisposition = response.headers['content-disposition'];

  if (!contentDisposition) {
    return fallbackType;
  }

  const fileTypeMatch = contentDisposition.match(/fileType=([^;]+)/i);

  if (fileTypeMatch && fileTypeMatch[1]) {
    const mimeFromFileType = getMimeFromExtension(fileTypeMatch[1]);
    if (mimeFromFileType) {
      return mimeFromFileType;
    }
  }

  const fileName = extractFileName(contentDisposition);

  if (fileName) {
    const extension = fileName.split('.').pop();
    const mimeFromExtension = getMimeFromExtension(extension);
    if (mimeFromExtension) {
      return mimeFromExtension;
    }
  }

  return fallbackType;
};

/**
 * Service function to fetch a client document PDF.
 *
 * @param {Object} params - The parameters for fetching the document.
 * @param {string} params.documentId - The ID of the document.
 * @returns {Promise<Blob>} - A promise that resolves to the PDF document blob.
 */
export const fetchClientDocument = async ({ documentId }) => {
  const url = buildBaseUrl(`b2c/documents/download/${documentId}`);
  const response = await axios.get(url, {
    headers: buildHeaders({ removeContentType: true }),
    responseType: 'blob',
    withCredentials: true,
  });

  const mimeType = resolveMimeType(response);
  const filename = filenameFromResponse(response);

  // A File rather than a Blob: File extends Blob, so callers that only want
  // the bytes are unaffected, and the name travels with them for the ones
  // that need it.
  return filename
    ? new File([response.data], filename, { type: mimeType })
    : new Blob([response.data], { type: mimeType });
};

/**
 * Service function to fetch an admin document PDF.
 *
 * @param {Object} params - The parameters for fetching the document.
 * @param {string} params.documentId - The ID of the document.
 * @param {string} params.userId - The ID of the user.
 * @returns {Promise<Blob>} - A promise that resolves to the PDF document blob.
 */
export const fetchAdminDocument = async ({ documentId }) => {
  const url = buildBaseUrl(`b2b/documents/download/${documentId}`);
  const response = await axios.get(url, {
    headers: buildHeaders({ removeContentType: true }),
    responseType: 'blob',
    withCredentials: true,
  });

  const mimeType = resolveMimeType(response);
  const filename = filenameFromResponse(response);

  // A File rather than a Blob: File extends Blob, so callers that only want
  // the bytes are unaffected, and the name travels with them for the ones
  // that need it.
  return filename
    ? new File([response.data], filename, { type: mimeType })
    : new Blob([response.data], { type: mimeType });
};

/**
 * Service function to fetch an admin document PDF.
 *
 * @param {Object} params - The parameters for fetching the document.
 * @param {string} params.quoteNumber - The quote number.
 * @returns {Promise<Blob>} - A promise that resolves to the PDF document blob.
 */
export const downloadQuote = async ({ applicationId, data }) => {
  //update this url for product and pricing
  const url = buildBaseUrl(`loan/product/download_quote/application/${applicationId}`);
  const response = await axios.post(url, data, {
    headers: buildHeaders(),
    responseType: 'blob',
    withCredentials: true,
  });

  return new Blob([response.data], { type: 'application/pdf' });
};
//API to download mortgage quote
export const downloadQuickPriceQuote = async ({ data }) => {
  //update this url for quick price
  const url = buildBaseUrl('loan/price/download_quote');
  const response = await axios.post(url, data, {
    headers: buildHeaders(),
    responseType: 'blob',
    withCredentials: true,
  });

  return new Blob([response.data], { type: 'application/pdf' });
};

export const downloadOffer = async ({ data, applicationId }) => {
  const url = buildBaseUrl(`b2b/loan/tools/download_quote/application/${applicationId}`);
  const response = await axios.post(url, data, {
    headers: buildHeaders(),
    responseType: 'blob',
    withCredentials: true,
  });

  return new Blob([response.data], { type: 'application/pdf' });
};

export const downloadConsumerOffer = async ({ data, applicationId }) => {
  const url = buildBaseUrl(`b2c/loan/tools/download_quote_consumer/application/${applicationId}`);
  const response = await axios.post(url, data, {
    headers: buildHeaders(),
    responseType: 'blob',
    withCredentials: true,
  });

  return new Blob([response.data], { type: 'application/pdf' });
};

export const downloadAmortization = async ({ data, applicationId }) => {
  const url = buildBaseUrl(`b2b/loan/tools/download_amortization/application/${applicationId}`);
  const response = await axios.post(url, data, {
    headers: buildHeaders(),
    responseType: 'blob',
    withCredentials: true,
  });

  return new Blob([response.data], { type: 'application/pdf' });
};

export const downloadConsumerAmortization = async ({ data, applicationId }) => {
  const url = buildBaseUrl(`b2c/loan/tools/download_amortization_consumer/application/${applicationId}`);
  const response = await axios.post(url, data, {
    headers: buildHeaders(),
    responseType: 'blob',
    withCredentials: true,
  });

  return new Blob([response.data], { type: 'application/pdf' });
};

/**
 * The pre-underwriting screen as a PDF — both the deterministic report and the
 * MIRA review, built server-side from the stored documents.
 *
 * 404 means nothing has been run for this application yet, which is a state the
 * caller shows rather than an error it reports.
 */
export const downloadPreUnderwritingReport = async ({ applicationId }) => {
  const url = buildBaseUrl(`b2b/loans/pre-underwriting/report-pdf/${applicationId}`);
  const response = await axios.get(url, {
    headers: buildHeaders({ removeContentType: true }),
    responseType: 'blob',
    withCredentials: true,
  });

  return {
    blob: new Blob([response.data], { type: 'application/pdf' }),
    fileName:
      extractFileName(response.headers?.['content-disposition'] || '') ||
      `pre-underwriting-${applicationId}.pdf`,
  };
};
